import {Injectable} from '@angular/core';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import {HttpHeaders, HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {HttpErrorHandlerService, HandleError} from './http-error-handling.service';
import {catchError} from 'rxjs/operators';
import {firstValueFrom, throwError} from 'rxjs';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': 'my-auth-token'
  })
};

@Injectable()
export class VxCoreService {
  private handleError: HandleError;

  constructor(private oauthService: OAuthService,
              private http: HttpClient) {

    this.oauthService.events.subscribe((event: OAuthEvent) => {
      const tokenUpdatedEvents = ['token_refreshed', 'token_received', 'silently_refreshed'];
      if (tokenUpdatedEvents.includes(event.type)) {
        httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
      }
    });

    httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
  }

  /**
   * Checks the eligibility of a VX Application
   * @param appId
   */
  checkVxApplicationEligibility(appId: number) {
    return this.http.get<any[]>(`${environment.api.vxCore}/applications/${appId}/further-advance-eligibility`, httpOptions)
      .pipe(
        catchError(error => {
          console.error('There was an error!', error);
          return throwError(error);
        })
      );
  }

  public checkTontoImported(tontoId) {
    return firstValueFrom(this.http.get(`${environment.api.vxCore}/applications/tonto/${tontoId}`, httpOptions));
  }

  public estimatedLvr(appId: number, data: any) {
    return this.http.post<any[]>(`${environment.api.vxCore}/applications/${appId}/lvr`, data, httpOptions);
  }

  private policyDatesCache: Promise<any> | null = null;

  /**
   * Checks whether the given policy is effective for a property acquired on `contractDate`.
   * @param policySlug - The policy identifier, e.g. `'negative-gearing-2026'`.
   * @param contractDate - The contract of sale date in DD/MM/YYYY format, or null if not yet set.
   * @returns `true` when `contractDate` is on or after the policy's `effectiveFrom` date.
   * The result of the `/configurations/policy-dates` fetch is cached for the lifetime of the
   * service instance (i.e. the browser session) — policy dates do not change mid-session.
   */
  public async isPolicyEffective(policySlug: string, contractDate: string | null): Promise<boolean> {
    if (!contractDate) { return false; }
    if (!this.policyDatesCache) {
      this.policyDatesCache = firstValueFrom(
        this.http.get<any>(`${environment.api.vxCore}/configurations/policy-dates`, httpOptions)
      );
    }
    const config = await this.policyDatesCache;
    const effectiveFrom = config?.config?.[policySlug]?.effectiveFrom;
    if (!effectiveFrom) { return false; }
    const [d, m, y] = contractDate.split('/');
    const contractMs = new Date(`${y}-${m}-${d}`).getTime();
    return contractMs >= new Date(effectiveFrom).getTime();
  }
}
